import { AuthFooter } from "components/design/authFooter";
import { Divider } from "components/design/Divider";
import Async from "components/DesignSystem/AsyncComponents/Async";
import { Button } from "components/DesignSystem/Button/Button";
import { TextInput } from "components/DesignSystem/TextInput/TextInput";
import { FormikForm } from "components/FormikForm/FormikForm";
import { SmallCaret } from "components/icons/SmallCaret";
import {
  SIGNUP_WITH_EMAIL,
  SIGNUP_WITH_GOOGLE,
} from "constants/analyticsEvents";
import { PUBLIC_USER_EMAIL, TASK_SESSION_ID } from "constants/session";
import { InvitationContext } from "contexts/InvitationContext";
import { signUp } from "formValidations/signupSchema";
import { useAnalytics } from "hooks/useAnalytics";
import { useAuth } from "hooks/useAuth";
import { useLocalStorage } from "hooks/useLocalStorage";
import { useQuery } from "hooks/useQuery";
import { useToast } from "hooks/useToast";
import { useUserOnboardingStepper } from "hooks/useUserOnboardingStepper";
import { useContext, useEffect, useState } from "react";
import "react-phone-input-2/lib/style.css";
import { useHistory, useLocation } from "react-router-dom";
import GoogleBtn from "signInSignUp/googleBtn";
import { setGoogleTosConsent } from "utils/googleTosConsent/setGoogleTosConsent";
import { useGetCrmUserQuery } from "store/apis/onboarding";
import "../../../static/styles/components/landingForm.css";
import { MagicLinkPayload } from "../magicLink";
import { ViewSignUp } from "./ViewSignup";

// constants
const clientId = process.env.PUBLIC_CURRENT_GOOGLE_CLIENT_ID;
const path = process.env.PUBLIC_REDIRECT_URL;

type SignUpPayload = MagicLinkPayload & {
  user_type?: string | null;
};

type SignUpProps = {
  updatePage: (body: SignUpPayload) => void;
  authLoading: boolean;
};

type SignUpFormData = {
  first_name: string;
  last_name: string;
  email: string;
};

export const SignUp = ({ updatePage, authLoading }: SignUpProps) => {
  const query = useQuery();
  const authtoken = useAuth();
  let userData = JSON.parse(localStorage.getItem("currentUser") || "{}");
  const userExist = Boolean(userData?.email || authtoken?.email);
  const { trackEvent } = useAnalytics();
  const [data, setData] = useState<SignUpFormData>({
    first_name: userData?.first_name || "",
    last_name: userData?.last_name || "",
    email: userData?.email || "",
  });
  const couponCode = query.get("couponCode") || query.get("couponcode");
  const refCode = query.get("ref");
  const taskSessionId = query.get("session_id");
  const { localData: refData, setItem: setReferralCodeData } = useLocalStorage<
    string | null
  >("referralCode", null);
  const isInvited = useContext(InvitationContext);
  const [isTermsPrivacySelected, setIsTermsPrivacySelected] = useState(false);
  const [showError, setShowError] = useState(false);
  const referralCode = refCode || refData;
  const history = useHistory();
  const { initialize } = useUserOnboardingStepper();

  const onToSPPSelection = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsTermsPrivacySelected(e.target.checked);
  };

  const { hash } = useLocation();
  const crmAccessToken = hash?.split("#payload=")[1];

  const {
    data: crmInvitedUser,
    isLoading,
    isError,
    error: inviteError,
    isSuccess,
  } = useGetCrmUserQuery(
    { crmAccessToken: crmAccessToken! },
    { skip: crmAccessToken === undefined || !isInvited }
  );

  const { alertToast } = useToast();

  useEffect(() => {
    if (isError && inviteError && "data" in inviteError) {
      const errorData = inviteError.data as { error?: { message?: string } };
      alertToast({ message: errorData?.error?.message });
    }
  }, [isError, inviteError, alertToast]);

  const userType = localStorage.getItem("user_type");

  useEffect(() => {
    if (taskSessionId) {
      localStorage.setItem(TASK_SESSION_ID, taskSessionId);
    }
  }, [taskSessionId]);

  useEffect(() => {
    document.title = "Sign Up | Inkle";
    if (couponCode) localStorage.setItem("couponCode", couponCode);
    if (referralCode) {
      setReferralCodeData(referralCode);
    }
  }, [couponCode, referralCode, setReferralCodeData]);

  const send = (values: SignUpFormData) => {
    if (isTermsPrivacySelected) {
      trackEvent(SIGNUP_WITH_EMAIL, {});
      const body: SignUpPayload = {
        first_name: values.first_name,
        last_name: values.last_name,
        email: values.email,
        user_type: userType,
        uuid: crmInvitedUser?.uuid,
      };
      initialize("SIGNUP");
      updatePage(body);
    } else {
      setShowError(true);
    }
  };

  const getAccessToken = () => {
    trackEvent(SIGNUP_WITH_GOOGLE, {});
    setGoogleTosConsent(isTermsPrivacySelected);
    window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?redirect_uri=${path}&prompt=consent&response_type=token&client_id=${clientId}&scope=openid%20email%20profile&flowName=GeneralOAuthFlow`;
  };

  const publicUserEmail = localStorage.getItem(PUBLIC_USER_EMAIL);

  useEffect(() => {
    setData((prev) => ({
      ...prev,
      email: crmInvitedUser?.email || publicUserEmail || "",
    }));
  }, [publicUserEmail, crmInvitedUser?.email]);

  const isPageLoading = isInvited && (isLoading || !data.email);

  if (userExist) {
    return <ViewSignUp />;
  }

  return (
    <Async.Root
      isLoading={isPageLoading}
      isSuccess={!isPageLoading}
      isEmpty={false}
    >
      <Async.Empty>
        <></>
      </Async.Empty>
      <Async.Success>
        <div className="t-flex t-flex-col t-gap-1 t-mb-8">
          <div className="t-text-subtitle-lg t-text-text-100">
            Let's set up your Inkle account
          </div>
          <div className="t-flex t-gap-2">
            <div className="t-text-body-sm t-text-text-30">
              Already have an account?
            </div>
            <Button customType="link" onClick={() => history.push("/signin")}>
              <div className="t-flex t-gap-2 t-items-center">
                <span>Sign In</span>
                <SmallCaret />
              </div>
            </Button>
          </div>
        </div>
        <FormikForm
          initialValues={data}
          validationSchema={signUp}
          onSubmit={send}
          validateOnBlur
          validateOnChange={false}
        >
          {({ errors }) => (
            <>
              <div className="t-flex t-flex-col t-gap-5">
                <div className="t-flex t-gap-4 sm:t-flex-col sm:t-gap-5">
                  <TextInput
                    customSize="regular"
                    name="first_name"
                    label="First name"
                    placeholder="John"
                  />
                  <TextInput
                    customSize="regular"
                    name="last_name"
                    label="Last name"
                    placeholder="Smith"
                  />
                </div>
                <TextInput
                  customSize="regular"
                  name="email"
                  label="Work email address"
                  placeholder="john.smith@company.com"
                  disabled={isInvited}
                />
                <AuthFooter
                  onChange={onToSPPSelection}
                  isChecked={isTermsPrivacySelected}
                  showError={!isTermsPrivacySelected && showError}
                />
              </div>

              <div className="t-mt-8 t-w-full">
                {isInvited ? (
                  <Button
                    customType="primary"
                    block
                    size="regular"
                    type="submit"
                    isLoading={authLoading}
                    disabled={authLoading}
                  >
                    Continue
                  </Button>
                ) : (
                  <Button
                    customType="primary"
                    block
                    size="regular"
                    type="submit"
                    isLoading={authLoading}
                    disabled={authLoading}
                  >
                    Sign up with email
                  </Button>
                )}
              </div>
            </>
          )}
        </FormikForm>
        {!isInvited && (
          <div>
            <div className="t-my-4 t-flex t-items-center t-justify-between t-gap-2">
              <Divider classNames="t-w-full" />
              <span className="t-text-body-sm t-text-text-30">or</span>
              <Divider classNames="t-w-full" />
            </div>
            <GoogleBtn onClick={getAccessToken} />
          </div>
        )}
      </Async.Success>
    </Async.Root>
  );
};
